import React, { useState } from 'react';
import { ActionTriggerField, Boolean, Calculation, Contact, ContactList, DateInput, Deadline, Dropdown, FileUpload, Instructions, MultiFile, MultiSelect, NumberInput, Subheader, Text, TimeInput, DocGen, SearchSelect } from "./FieldComponents";
import { MultiVal } from './MultiVal';
import MatterPublicFormField from './MatterPublicFormField';

export const normalizeValueToIndex = (field, value) => {
    if (!field.options) return value;

    let options = [];
    try {
        options = Array.isArray(field.options)
            ? field.options
            : JSON.parse(field.options);
    } catch {
        return value;
    }

    if (typeof value === "string") {
        const index = options.findIndex(opt => opt.toLowerCase?.() === value.toLowerCase?.());
        return index !== -1 ? index : value;
    }

    return value;
};

export const CustomField = ({
    field,
    value,
    onChange,
    conditional = false,
    formData = {},
    fieldUpdates = {},
    allFields = [],
    lead_id,
    case_id,
    refreshAfterCalc,
    sectionName,
    setNewContactFieldId,
    setCreateContact,
    handleEditContact,
    caseType,
    caseData = null
}) => {
    if (!field) return null;

    // Debug field 4 specifically
    if (field.id === 4) {
        console.log('CustomField props for field 4:', {
            fieldId: field.id,
            valueProp: value,
            valuePropType: typeof value,
            fieldUpdatesObject: fieldUpdates,
            fieldUpdatesKeys: Object.keys(fieldUpdates),
            formDataObject: formData,
            formDataKeys: Object.keys(formData)
        });
    }

    const resolveCalculationValue = (fieldId, visited = new Set()) => {
        if (visited.has(fieldId)) {
            console.warn(`Circular dependency detected for field ID: ${fieldId}`);
            return 0;
        }
    
        visited.add(fieldId);
    
        const fieldObj = allFields.find(f => f.id === fieldId);
        if (!fieldObj || fieldObj.field_id !== 17) return getFieldValue(fieldObj);
    
        const options = typeof fieldObj.options === 'string' ? JSON.parse(fieldObj.options) : fieldObj.options;
        const dependentFieldIds = JSON.parse(options?.field_ids || '[]');
        const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
        let expression = options?.rule || '';
    
        dependentFieldIds.forEach((id, idx) => {
            let value = resolveCalculationValue(id, visited);
            if (value === '' || value === null || value === undefined) {
                const operatorBefore = expression[expression.indexOf(letters[idx]) - 1];
                value = operatorBefore === '*' || operatorBefore === '/' ? 1 : 0;
            }
            expression = expression.replaceAll(letters[idx], value);
        });
    
        try {
            if (!expression || /[^0-9+\-*/(). ]/.test(expression) || /[+\-*/]$/.test(expression)) {
                throw new SyntaxError(`Invalid or incomplete expression: "${expression}"`);
            }
    
            const result = new Function(`return ${expression}`)();
            return typeof result === 'number' ? parseFloat(result.toFixed(2)) : 0;
        } catch (e) {
            console.error(`Error evaluating calculation for field ID: ${fieldId}`, e, { expression });
            return 0;
        }
    };
    
    const getFieldValue = (field) => {
        if (!field) return '';
        
        const debugInfo = {
            fieldId: field.id,
            fieldName: field.name,
            addItem: field.add_item,
            formDataValue: formData[field.id],
            fieldUpdatesValue: fieldUpdates?.[field.id],
            fieldType: field.field_id
        };
        
        if (field.id === 4) {
            console.log('getFieldValue debug for field 4:', debugInfo);
            console.log('fieldUpdates object keys and values:', {
                keys: Object.keys(fieldUpdates || {}),
                values: Object.values(fieldUpdates || {}),
                hasKey4AsNumber: fieldUpdates?.[4],
                hasKey4AsString: fieldUpdates?.["4"],
                typeOfKey4: typeof Object.keys(fieldUpdates || {}).find(k => k == 4)
            });
        }
        
        if (field.add_item === 1) {
            return formData[field.id];
        } else {
            const val = formData[field.id];
            if (val !== undefined && val !== null) return val;

            // Check for both numeric and string keys to maintain backward compatibility
            const stored = fieldUpdates?.[field.id] ?? fieldUpdates?.[String(field.id)];
            if (stored !== undefined) return stored;            if (field.field_id === 17) {
                return resolveCalculationValue(field.id);
            }
    
            if (field.field_id === 12) return 2;
    
            return '';
        }
    };

    const shouldDisplay = (field) => {
        if ((field.add_item === 1 && !formData.add_item_mode) || field.hidden === 2) return false;
    
        if (Number(field.case_type_id) !== Number(caseType) && Number(field.case_type_id) !== 0) return false;
    
        // Handle database expressions like "leads.marketing_source = 33"
        if (field.is_answered && field.is_answered.includes('=') && caseData) {
            try {
                // Parse expressions like "leads.marketing_source = 33"
                const expression = field.is_answered.trim();
                const [leftSide, operator, rightSide] = expression.split(/\s*([=!<>]+)\s*/);
                
                if (leftSide && operator && rightSide) {
                    // Extract the path like "leads.marketing_source"
                    const pathParts = leftSide.split('.');
                    let currentValue = caseData;
                    
                    // Navigate through the object path
                    for (const part of pathParts) {
                        if (currentValue && typeof currentValue === 'object') {
                            currentValue = currentValue[part];
                        } else {
                            currentValue = undefined;
                            break;
                        }
                    }
                    
                    // Compare values based on operator
                    const expectedValue = isNaN(rightSide) ? rightSide : Number(rightSide);
                    const actualValue = isNaN(currentValue) ? currentValue : Number(currentValue);
                    
                    switch (operator) {
                        case '=':
                        case '==':
                            return actualValue == expectedValue;
                        case '!=':
                        case '<>':
                            return actualValue != expectedValue;
                        case '>':
                            return actualValue > expectedValue;
                        case '<':
                            return actualValue < expectedValue;
                        case '>=':
                            return actualValue >= expectedValue;
                        case '<=':
                            return actualValue <= expectedValue;
                        default:
                            console.warn(`Unknown operator in is_answered: ${operator}`);
                            return true;
                    }
                }
            } catch (error) {
                console.error('Error parsing is_answered expression:', field.is_answered, error);
                return true;
            }
        }
    
        // If no display_when condition, show the field
        if (!field.display_when && !field.display_multi) return true;

        // Check for logical operators in multi_criteria first, fallback to display_when/display_multi
        let criteriaStr = field.multi_criteria ? String(field.multi_criteria) : String(field.display_multi || field.display_when || '');
        
        // Parse as JSON if it's an array format
        let displayWhenArray = null;
        try {
            const parsed = JSON.parse(criteriaStr);
            if (Array.isArray(parsed)) {
                displayWhenArray = parsed;
                criteriaStr = parsed.join(' && '); // Treat arrays as AND conditions
            }
        } catch (e) {
            // Not JSON, continue with string format
        }
        
        const hasAndOperator = criteriaStr.includes('&&');
        const hasOrOperator = criteriaStr.includes('||');
        
        if (hasAndOperator || hasOrOperator || displayWhenArray) {
            // Handle multiple conditions with logical operators
            const operator = hasAndOperator ? '&&' : '||';
            const fieldIds = displayWhenArray || criteriaStr.split(operator).map(id => parseInt(id.trim()));
            const isAnsweredArray = Array.isArray(field.is_answered) ? field.is_answered : 
                                    (field.is_answered ? field.is_answered.split(operator) : []);
            
            // Evaluate each condition
            const results = fieldIds.map((fieldId, index) => {
                const controllingField = allFields.find(f => f.id === fieldId);
                if (!controllingField) return false;
                
                const controllingValue = getFieldValue(controllingField);
                const condition = isAnsweredArray[index] ? String(isAnsweredArray[index]).trim() : '';
                
                // Handle special conditions
                if (condition === '[!]') {
                    return controllingValue === null || controllingValue === undefined || controllingValue === "";
                }
                
                if (condition === "" || condition === null) {
                    return controllingValue !== null && controllingValue !== undefined && controllingValue !== "";
                }
                
                const normalizedValue = normalizeValueToIndex(controllingField, controllingValue);
                
                let parsedAnswers = [];
                try {
                    const parsed = JSON.parse(condition);
                    if (Array.isArray(parsed)) parsedAnswers = parsed.map(v => String(v));
                    else parsedAnswers = [String(parsed)];
                } catch {
                    parsedAnswers = String(condition).split(',').map(v => v.trim());
                }
                
                const isNegated = parsedAnswers.every(answer => String(answer).startsWith('!'));
                const cleanAnswers = parsedAnswers.map((v) => String(v).replace(/^!/, ''));

                if (isNegated) {
                    return !cleanAnswers.includes(String(normalizedValue));
                } else {
                    return cleanAnswers.includes(String(normalizedValue));
                }
            });
            
            // Apply logical operator to results
            if (operator === '&&' || displayWhenArray) {
                return results.every(result => result === true);
            } else { // ||
                return results.some(result => result === true);
            }
        } else {
            // Handle single condition (original logic)
            let fieldIdToCheck = null;
            try {
                const parsed = JSON.parse(criteriaStr);
                fieldIdToCheck = parseInt(parsed);
            } catch (e) {
                fieldIdToCheck = parseInt(field.display_multi || field.display_when);
            }
            
            const controllingField = allFields.find(f => f.id === fieldIdToCheck);
            if (!controllingField) return true;
            
            let controllingValue = getFieldValue(controllingField);

            if (field.is_answered === '[!]') {
                return controllingValue === null || controllingValue === undefined || controllingValue === "";
            }
        
            if (field.is_answered === null || field.is_answered === "") {
                return controllingValue !== null && controllingValue !== undefined && controllingValue !== "";
            }
        
            controllingValue = normalizeValueToIndex(controllingField, controllingValue);
        
            let parsedAnswers = [];
            try {
                const parsed = JSON.parse(field.is_answered);
                if (Array.isArray(parsed)) parsedAnswers = parsed.map(v => String(v));
                else parsedAnswers = [String(parsed)];
            } catch {
                parsedAnswers = String(field.is_answered).split(',').map(v => v.trim());
            }
            
            const isNegated = parsedAnswers.every(answer => String(answer).startsWith('!'));
            const cleanAnswers = parsedAnswers.map((v) => String(v).replace(/^!/, ''));

            if (isNegated) {
                return !cleanAnswers.includes(String(controllingValue));
            } else {
                return cleanAnswers.includes(String(controllingValue));
            }
        }
    };

    const handleFieldChange = (fieldId, val) => {
        onChange(fieldId, val);
    };

    const renderFieldInput = (field) => {
        switch (field.field_id) {
            case 1:
                return <Text type="text" placeholder={field.name} value={value} onChange={(val) => handleFieldChange(field.id, val)} />;
            case 2:
                return <Text type="textarea" placeholder={field.name} value={value} onChange={(val) => handleFieldChange(field.id, val)} />;
            case 3:
                return <NumberInput type="currency" value={value} onChange={(val) => handleFieldChange(field.id, val)} />;
            case 4:
                return <NumberInput type="percent" value={value} onChange={(val) => handleFieldChange(field.id, val)}/>;
            case 5:
                return <NumberInput type="number" value={value} onChange={(val) => handleFieldChange(field.id, val)}/>;
            case 6:
                return <DateInput value={String(value && String(value).includes(" ") ? String(value).split(" ")[0] : value || '')} onChange={(val) => handleFieldChange(field.id, val)}/>;
            case 7:
                return <TimeInput value={value} onChange={(val) => handleFieldChange(field.id, val)}/>;
            case 8:
                return (
                    <Contact
                        selectedContact={value}
                        setSelectedContact={(contact) => handleFieldChange(field.id, contact?.id || null)}
                        onCreateNewContact={() => {
                            if (setNewContactFieldId && setCreateContact) {
                                setNewContactFieldId(field.id);
                                setCreateContact(true);
                            }
                        }}
                        onEditContact={handleEditContact}
                    />
                );
            case 9:
                // Parse ContactList value - it can be an array, a JSON string array, or individual values
                let contactIds = [];
                try {
                    let parsedValue = value;
                    
                    // If value is a string, try to parse it as JSON
                    if (typeof value === 'string' && value.trim() !== '') {
                        parsedValue = JSON.parse(value);
                    }
                    
                    // Ensure we have an array
                    if (!Array.isArray(parsedValue)) {
                        parsedValue = parsedValue ? [parsedValue] : [];
                    }
                    
                    // Extract IDs from the array
                    parsedValue.forEach(item => {
                        const id = typeof item === 'object' && item !== null ? item.id : item;
                        if (id) {
                            contactIds.push(Number(id));
                        }
                    });
                } catch (e) {
                    console.error('Error parsing ContactList value in CustomField:', value, e);
                    contactIds = Array.isArray(value) ? value : [];
                }
                
                return (
                    <ContactList
                        value={contactIds}
                        onChange={(ids) => handleFieldChange(field.id, ids)}
                        onCreateNewContact={() => {
                            if (setNewContactFieldId && setCreateContact) {
                                setNewContactFieldId(field.id);
                                setCreateContact(true);
                            }
                        }}
                        onEditContact={handleEditContact}
                    />
                );
            case 10:
                console.log('Dropdown field debug:', {
                    fieldId: field.id,
                    fieldName: field.name,
                    options: field.options,
                    parsedOptions: JSON.parse(field.options) || "[]",
                    value: value,
                    valueType: typeof value,
                    formDataValue: formData[field.id],
                    fieldUpdatesValue: fieldUpdates[field.id]
                });
                return <Dropdown
                            options={JSON.parse(field.options) || "[]"}
                            value={value}
                            onChange={(index) => handleFieldChange(field.id, index)}
                            marketing_list={field.id === 225}
                            placeholder={field.name}
                        />;
            case 11:
                return <MultiSelect 
                    options={field.options || "[]"} 
                    value={value} 
                    onChange={(val) => handleFieldChange(field.id, val)} 
                />;
            case 12:
                return <Boolean 
                    options={field.options || []} 
                    value={value !== undefined && value !== null && value !== '' ? Number(value) : 2} 
                    onChange={(selectedValue) => handleFieldChange(field.id, selectedValue)} 
                />;
            case 13:
                return <Subheader title={field.name} />;
            case 14:
                return <Instructions instructions={field.name}/>;
            case 15:
                return <FileUpload
                    value={value}
                    onChange={(val) => handleFieldChange(field.id, val)}
                    lead_id={lead_id}
                    section_name={sectionName}
                />;
            case 16:
                return <MultiFile
                    value={value}
                    lead_id={lead_id}
                    sectionName={sectionName}
                    onChange={(val) => handleFieldChange(field.id, val)}
                />;
            case 17:
                return (
                    <Calculation
                        options={field.options}
                        fieldId={field.id}
                        lead_id={lead_id}
                        fields={allFields}
                        formData={formData}
                        fieldUpdates={fieldUpdates}
                        onChange={(val) => refreshAfterCalc ? refreshAfterCalc(field.id, val) : handleFieldChange(field.id, val)}
                    />
                );                
            case 18:
                return <DocGen
                            value={value}
                            onChange={(val) => handleFieldChange(field.id, val)}
                            options={field.docgen_templates || []}
                            lead_id={lead_id}
                            case_id={case_id}
                            field_id={field.id}
                            formData={fieldUpdates}
                            newData={formData}
                            fields={allFields}
                        />;
            case 20:
                let parsedValue = value || {};
                if (typeof value === 'string') {
                    try {
                        parsedValue = JSON.parse(value || '{}');
                    } catch (error) {
                        parsedValue = { due: value || '', done: '' };
                    }
                }
                return <Deadline 
                            value={{ due: parsedValue.due || '', done: parsedValue.done || '' }}
                            title={field.name}
                            onChange={(updated) => handleFieldChange(field.id, {
                                ...parsedValue,
                                ...updated
                            })}
                        />;
            case 22:
                return <SearchSelect
                            value={value}
                            onChange={(val) => handleFieldChange(field.id, val)}
                            options={field.options}
                            placeholder={field.name}
                        />;
            case 23:
                return <ActionTriggerField
                            field={field}
                            value={value}
                            leadId={lead_id}
                            caseId={case_id}
                            groupId={formData?.group_id ?? null}
                        />;
            case 26:
                return <MultiVal
                            value={value}
                            options={field.options || '[]'}
                            fieldName={field.name}
                            onChange={(val) => handleFieldChange(field.id, val)}
                        />;
            case 28:
                return <MatterPublicFormField
                            field={field}
                            leadId={lead_id}
                            caseId={case_id}
                            groupId={formData?.group_id ?? null}
                        />;
            default:
                return null;
        }
    };

    if (field && !shouldDisplay(field)) {
        return null;
    }

    return ![13, 14, 20, 23, 26].includes(field.field_id) ? (
        <div id={`field-${field.id}`} className={`form-group nm ${conditional === false ? '' : 'conditional'}`} style={{ scrollMarginTop: '120px' }}>
            <label className='subtext'>{field.name}</label>
            {renderFieldInput(field)}
        </div>
    ) : renderFieldInput(field);
};

export const FieldGroup = ({
    fields = [],
    formData = {},
    fieldUpdates = {},
    onChange,
    lead_id,
    case_id,
    refreshAfterCalc,
    sectionName,
    setNewContactFieldId,
    setCreateContact,
    handleEditContact,
    caseType,
    caseData = null
}) => {
    if (!fields || fields.length === 0) return null;

    // Debug fieldUpdates structure
    console.log('FieldGroup fieldUpdates:', {
        fieldUpdatesObject: fieldUpdates,
        fieldUpdatesKeys: Object.keys(fieldUpdates),
        field4Value: fieldUpdates[4],
        field4StringValue: fieldUpdates['4']
    });

    const dependencyMap = {};
    fields.forEach(field => {
        if (field.display_when) {
            // Use multi_criteria first, fallback to display_when if multi_criteria is NULL
            const criteriaStr = field.multi_criteria ? String(field.multi_criteria) : String(field.display_when);
            
            // Check for logical operators in criteria
            const hasAndOperator = criteriaStr.includes('&&');
            const hasOrOperator = criteriaStr.includes('||');
            
            if (hasAndOperator || hasOrOperator) {
                // For fields with logical operators, group them under the last field ID
                const operator = hasAndOperator ? '&&' : '||';
                const fieldIds = criteriaStr.split(operator).map(id => parseInt(id.trim()));
                const lastFieldId = fieldIds[fieldIds.length - 1]; // Use the last field ID for grouping
                
                if (!dependencyMap[lastFieldId]) dependencyMap[lastFieldId] = [];
                dependencyMap[lastFieldId].push(field);
            } else {
                // Handle single field dependency (original logic)
                const fieldId = parseInt(field.display_when);
                if (!dependencyMap[fieldId]) dependencyMap[fieldId] = [];
                dependencyMap[fieldId].push(field);
            }
        }
    });

    const rendered = new Set();
    const output = [];
    let currentSubSection = [];
    let currentInstructionGroup = [];

    const flushSubSection = () => {
        if (currentSubSection.length > 0) {
            if (currentSubSection[0]?.props?.field?.field_id === 13) {
                output.push(
                    <React.Fragment key={`frag-${output.length}`}>
                        {currentSubSection}
                    </React.Fragment>
                );
            } else {
                output.push(
                    <div className="sub-section" key={`sub-${output.length}`}>
                        {currentSubSection}
                    </div>
                );
            }
            currentSubSection = [];
        }
    };

    const flushInstructionGroup = () => {
        if (currentInstructionGroup.length > 0) {
            output.push(
                <div className="instruction-group" key={`instruction-${output.length}`}>
                    {currentInstructionGroup}
                </div>
            );
            currentInstructionGroup = [];
        }
    };

    let currentSubGroup = [];
    let currentBooleanFieldId = null;
    
    const flushSubGroup = () => {
        if (currentSubGroup.length > 0 && currentBooleanFieldId !== null) {
            const validSubGroupContent = currentSubGroup.filter(content => content !== null && content !== undefined);
            
            if (validSubGroupContent.length > 0) {
                const subGroupContent = (
                    <div className={`sub-boolean field-${currentBooleanFieldId}`} key={`sub-boolean-${currentBooleanFieldId}`}>
                        {validSubGroupContent}
                    </div>
                );
                currentSubSection.push(subGroupContent);
            }
        }
        
        currentSubGroup = [];
        currentBooleanFieldId = null;
    };

    fields
        .filter(f => f.hidden !== 2 && f.obsolete !== 2)
        .sort((a, b) => a.order_id - b.order_id)
        .forEach((field, index) => {
            if (rendered.has(field.id)) return;

            const fieldEl = (
                <CustomField
                    key={`${field.id}-${index}`}
                    field={field}
                    value={formData[field.id] !== undefined && formData[field.id] !== null ? formData[field.id] : (fieldUpdates[field.id] !== undefined ? fieldUpdates[field.id] : '')}
                    onChange={onChange}
                    conditional={false}
                    formData={formData}
                    fieldUpdates={fieldUpdates}
                    allFields={fields}
                    lead_id={lead_id}
                    case_id={case_id}
                    refreshAfterCalc={refreshAfterCalc}
                    sectionName={sectionName}
                    setNewContactFieldId={setNewContactFieldId}
                    setCreateContact={setCreateContact}
                    handleEditContact={handleEditContact}
                    caseType={caseType}
                    caseData={caseData}
                />
            );

            rendered.add(field.id);

            if (field.field_id === 13) {
                flushSubGroup();
                flushInstructionGroup();
                flushSubSection();
                currentSubSection.push(fieldEl);
                flushSubSection();
            } else if (field.field_id === 14) {
                flushSubGroup();
                flushSubSection();
                currentInstructionGroup.push(fieldEl);
            } else if (field.field_id === 12 && field.display_when == null) {
                flushSubGroup();
                flushInstructionGroup();
                currentBooleanFieldId = field.id;
                currentSubGroup.push(fieldEl);
            } else {
                if (currentBooleanFieldId && field.display_when === currentBooleanFieldId) {
                    currentSubGroup.push(fieldEl);
                } else {
                    flushSubGroup();
                    flushInstructionGroup();
                    currentSubSection.push(fieldEl);
                }
            }
        });

    flushSubGroup();
    flushSubSection();
    flushInstructionGroup();

    return <>{output}</>;
};

export default CustomField;
